import React, { useCallback } from 'react';
import { AppButton, Box, PencilIcon } from '@common-fe/common-fe';

import Permissions from '@/common/permissions';
import { useHasAccess } from '@/modules/core';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { ButtonHintForTerminatedEmployer } from '@/modules/employer/components/EmployerTermination/ButtonHintForTerminatedEmployer';

import TopbarIconButton from '../EmployeeTopbar/TopbarIconButton';
import { useValidationEmployee } from '../hooks';

interface Props {
  isSubmitted?: boolean;
  isEditMode?: boolean;
  onToggleEdit: (value: boolean) => void;
  onToggleSubmit: (value: boolean) => void;
}
const EmployeeSave: React.FC<Props> = ({
  onToggleEdit, isEditMode,
  onToggleSubmit, isSubmitted,
}) => {
  const { handleValidate: handleSave, isErrors } = useValidationEmployee();
  const handleToggleEditMode = useCallback(() => {
    onToggleEdit(!isEditMode);
  }, [isEditMode, onToggleEdit]);

  const handleValidate = useCallback(async () => {
    onToggleSubmit(true);
    if (isErrors) {
      return;
    }
    try {
      const isSuccess = await handleSave();
      if (isSuccess) {
        onToggleEdit(false);
      }

    // eslint-disable-next-line no-empty
    } catch {}
  }, [handleSave, isErrors, onToggleEdit, onToggleSubmit]);

  const hasAccessToEditEmployee = useHasAccess([{ permission: Permissions.EDIT_EE_DATA }]);
  const { isLimitedUseWhileTerminationInProgress } = useEmployerTerminationBanner();

  if (!hasAccessToEditEmployee) return null;

  return (
    <Box width={{ min: 'auto' }}>
      {isEditMode ? (
        <AppButton
          width="140px"
          disabled={isErrors && isSubmitted}
          onClick={handleValidate}
          testId="save-employee"
        >
          Save
        </AppButton>
      ) : (
        <ButtonHintForTerminatedEmployer
          button={
            <TopbarIconButton
              testId="edit-employee"
              title="Edit"
              icon={(
                <PencilIcon
                  size="24px"
                  color={isLimitedUseWhileTerminationInProgress ? 'iconSecondary' : 'iconPrimary'}
                />
              )}
              onClick={handleToggleEditMode}
              disabled={isLimitedUseWhileTerminationInProgress}
            />
          }
          isTerminatedEmployer={isLimitedUseWhileTerminationInProgress}
          hintText='Terminated Organization cannot edit employee'
        />
      )}
    </Box>
  );
};

export default EmployeeSave;
